import type { KitField } from "../../form/kitFieldRegistry";
import { parseIsoOffset, toIsoLocal } from "./dateTime";
import { isSettableKind } from "./types";
import type {
  ApplyOutcome,
  BridgeValue,
  DiscoveredKind,
  FieldAdapter,
  FieldDescriptor,
  FieldOption,
  FieldValue,
  FieldValueElement,
  RejectionDetail,
} from "./types";

function isScalar(value: unknown): value is FieldValueElement {
  return typeof value === "string" || typeof value === "number" || typeof value === "boolean";
}

function isString(value: unknown): value is string {
  return typeof value === "string";
}

/** A copy of the array when every element passes the check; an empty model reads as an empty selection. */
function arrayOf<T extends FieldValueElement>(value: unknown, isElement: (element: unknown) => element is T): T[] | undefined {
  if (value == null) {
    return [];
  }
  return Array.isArray(value) && value.every(isElement) ? [...value] : undefined;
}

/**
 * A model value in the shape the wire carries for the kind: the selected option values of a
 * multi-select, the strings of a tag input, a date picker's ISO date-time, a scalar otherwise.
 * Undefined when the model holds something else — an object a converter did not map, a nested list.
 */
export function toBridgeValue(kind: DiscoveredKind, value: unknown): BridgeValue {
  switch (kind) {
    case "multiselect":
      return arrayOf(value, isScalar);
    case "tags":
      return arrayOf(value, isString);
    case "datetime":
      return toIsoLocal(value);
    default:
      if (value === null) {
        return null;
      }
      return isScalar(value) ? value : undefined;
  }
}

/** Kinds whose model value has a shape of its own; a model outside that shape leaves the input unsupported. */
const SHAPED_KINDS: ReadonlySet<DiscoveredKind> = new Set<DiscoveredKind>(["multiselect", "tags", "datetime"]);

function kindOf(handle: KitField): DiscoveredKind {
  const kind = handle.kind();
  return SHAPED_KINDS.has(kind) && toBridgeValue(kind, handle.currentValue()) === undefined ? "unsupported" : kind;
}

type ModelValue = { ok: true; value: unknown } | { ok: false; detail: RejectionDetail };

/**
 * A wire value in the model space the input's own change path takes: a date picker holds epoch
 * millis, not the ISO text the wire carries. A tag input never receives null — react-tagsinput
 * cannot render one — so an empty value is an empty list.
 */
function toModelValue(kind: DiscoveredKind, value: BridgeValue): ModelValue {
  if (kind === "datetime" && typeof value === "string") {
    const epochMillis = parseIsoOffset(value);
    return epochMillis === undefined ? { ok: false, detail: "not-a-date-time" } : { ok: true, value: epochMillis };
  }
  if (kind === "tags" && value == null) {
    return { ok: true, value: [] };
  }
  return { ok: true, value };
}

function toOption(option: { value: unknown; label: string }): FieldOption {
  const value = option.value == null ? null : isScalar(option.value) ? option.value : String(option.value);
  return { value, label: option.label };
}

/**
 * Describes and fills a form-kit input through its registry handle: model-space values (the
 * number 30, not the string "30"), the full option list as `{ value, label }` pairs, the input's
 * own validators before anything changes, and the input's own change path into the page's
 * `formDidUpdate`. The element is only used to carry the marker.
 */
export function createKitFieldAdapter(handle: KitField, element: HTMLElement | undefined, path: string): FieldAdapter {
  const kind = kindOf(handle);

  const options = (): FieldOption[] | undefined => {
    const list = handle.options();
    return list ? list.map(toOption) : undefined;
  };

  const currentValue = (): BridgeValue => toBridgeValue(kind, handle.currentValue());

  const error = (): string | undefined => {
    if (!handle.model || handle.fieldSet.isModelValid(handle.model)) {
      return undefined;
    }
    return handle.validate(handle.currentValue()).errorName ?? "invalid";
  };

  const describe = (): FieldDescriptor => {
    const descriptor: FieldDescriptor = { path, kind: kind === "unsupported" ? "text" : kind, label: path };
    const value = currentValue();
    if (value !== undefined) {
      descriptor.value = value;
    }
    const optionList = options();
    if (optionList) {
      descriptor.options = optionList;
    }
    if (handle.isRequired()) {
      descriptor.required = true;
    }
    if (handle.isReadOnly()) {
      descriptor.readOnly = true;
    }
    const validationError = error();
    if (validationError) {
      descriptor.error = validationError;
    }
    return descriptor;
  };

  const restore = (value: BridgeValue): void => {
    const modelValue = toModelValue(kind, value);
    if (modelValue.ok) {
      handle.apply(modelValue.value);
    }
  };

  const apply = (value: FieldValue): ApplyOutcome => {
    if (!isSettableKind(kind)) {
      return { applied: false, reason: "unsupported" };
    }
    const modelValue = toModelValue(kind, value);
    if (!modelValue.ok) {
      return { applied: false, reason: "invalid", detail: modelValue.detail };
    }
    const result = handle.validate(modelValue.value);
    if (result.errorName) {
      return { applied: false, reason: "invalid", detail: "validator", validator: result.errorName };
    }
    handle.apply(modelValue.value);
    return { applied: true };
  };

  return { element, kind, options, currentValue, describe, apply, restore };
}
